import { EnergyDataPoint } from '../types/energy';
import { jstTodayIso, normalizeDailyTimestamp, normalizeMonthlyTimestamp } from './telemetryPeriod';

/**
 * The portal's 12-month chart stops at the last finalized month, so the month still running never
 * arrives as a monthly row. Its only measurements are the daily rows, and summing those is the sole
 * honest way to fill it in.
 *
 * A chart month asserts kWh and nothing else (AI_RULES.md), so the derived row carries no cost, no
 * 検針日 and no 使用日数, and it is never `statementConfirmed` — the statement ledger keeps reading
 * the real monthly rows only, so a month-to-date sum can never present itself as a bill.
 */
export function buildMonthToDateUsage(
  dailyUsage: EnergyDataPoint[],
  monthlyUsage: EnergyDataPoint[],
  todayIso: string = jstTodayIso()
): EnergyDataPoint | null {
  const monthKey = /^\d{4}-\d{2}/.test(todayIso) ? todayIso.slice(0, 7) : '';
  if (!monthKey) return null;

  // A portal month row always wins, whether it came from the chart or the confirmed ledger.
  if (monthlyUsage.some((row) => normalizeMonthlyTimestamp(row.timestamp) === monthKey)) return null;

  const days = dailyUsage.filter((row) => normalizeDailyTimestamp(row.timestamp)?.startsWith(monthKey));
  // Days the axis plots but the portal has not measured read as 0, so an all-zero month is no data.
  if (!days.some((row) => (row.kwh ?? 0) > 0)) return null;

  const sumTier = (pick: (row: EnergyDataPoint) => number | undefined) =>
    Number(days.reduce((acc, row) => acc + (pick(row) ?? 0), 0).toFixed(1));

  const [year, month] = monthKey.split('-');
  return {
    timestamp: monthKey,
    label: `${year}年${Number(month)}月`,
    kwh: sumTier((row) => row.kwh),
    cost: 0,
    dayKwh: sumTier((row) => row.dayKwh),
    lifeKwh: sumTier((row) => row.lifeKwh),
    nightKwh: sumTier((row) => row.nightKwh),
    isPeak: false,
    monthToDate: true
  };
}
